import { useMyOrganizations } from "@/api/organizations/useMyOrganizations";
import { supabase } from "@/api/supabaseClient";
import { useAuth } from "@/auth/AuthContext";
import { LOADING_LABEL } from "@/constants/uiLabels";
import { useTheme } from "@/theme/ThemeProvider";
import { AppButton } from "@/ui/components/AppButton";
import { DenseRow } from "@/ui/components/DenseRow";
import { EmptyState } from "@/ui/components/EmptyState";
import { FloatingLabelInput } from "@/ui/components/FloatingLabelInput";
import { Screen } from "@/ui/components/Screen";
import { SectionCard } from "@/ui/components/SectionCard";
import { SoftSection } from "@/ui/components/SoftSection";
import { viewWhileLoading } from "@/ui/loadingState";
import { useRouter } from "expo-router";
import { useEffect, useState } from "react";
import { Text, View } from "react-native";

/**
 * Profil: Anzeigename, Theme, Organisation, Abmelden.
 * Abmelden gibt es nur hier, nicht zusätzlich in der Navigation.
 */
export default function ProfileScreen() {
  const { theme, mode, setMode } = useTheme();
  const { user, signOut } = useAuth();
  const { organizations, loading: orgsLoading } = useMyOrganizations();
  const router = useRouter();
  const [displayName, setDisplayName] = useState("");
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!user?.id) return;
      const { data } = await supabase
        .from("profiles")
        .select("display_name")
        .eq("id", user.id)
        .maybeSingle();
      if (!cancelled && data?.display_name) setDisplayName(data.display_name);
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  const save = async () => {
    if (!user?.id) return;
    setSaving(true);
    setMessage(null);
    const { error } = await supabase
      .from("profiles")
      .update({ display_name: displayName.trim() || null })
      .eq("id", user.id);
    setSaving(false);
    setMessage(error ? "Speichern fehlgeschlagen." : "Gespeichert.");
  };

  return (
    <Screen title="Profil" scroll contentStyle={{ paddingBottom: theme.space.xxl }}>
      <View style={{ gap: theme.space.md }}>
        <Text
          style={{
            color: theme.color.muted,
            fontSize: theme.fontSize.small,
          }}
        >
          Konto, Darstellung und Organisationsmitgliedschaften.
        </Text>

        <SectionCard title="Konto">
          <View style={{ gap: theme.space.md }}>
            <Text
              style={{
                color: theme.color.muted,
                fontSize: theme.fontSize.small,
              }}
            >
              {user?.email ?? "–"}
            </Text>
            <FloatingLabelInput
              label="Anzeigename"
              value={displayName}
              onChangeText={setDisplayName}
            />
            <AppButton onPress={save} disabled={saving}>
              {saving ? "Speichern…" : "Speichern"}
            </AppButton>
            {message ? (
              <Text style={{ color: theme.color.muted, fontSize: theme.fontSize.small }}>
                {message}
              </Text>
            ) : null}
          </View>
        </SectionCard>

        <SectionCard title="Darstellung">
          <AppButton
            variant="outline"
            onPress={() => setMode(mode === "light" ? "dark" : "light")}
          >
            {mode === "light" ? "Dunklen Modus aktivieren" : "Hellen Modus aktivieren"}
          </AppButton>
        </SectionCard>

        <SoftSection title="Organisationen">
          {viewWhileLoading(orgsLoading, organizations.length === 0) === "loading" ? (
            <Text style={{ color: theme.color.muted }}>{LOADING_LABEL}</Text>
          ) : viewWhileLoading(orgsLoading, organizations.length === 0) === "empty" ? (
            <EmptyState
              title="Keine aktive Mitgliedschaft."
              hint="Sobald du einem Verein beitrittst, erscheint er hier."
            />
          ) : (
            organizations.map((o) => (
              <DenseRow key={o.id} title={o.name} meta="Aktiv" />
            ))
          )}
        </SoftSection>

        <AppButton
          variant="outline"
          onPress={async () => {
            await signOut();
            router.replace("/login" as any);
          }}
        >
          Abmelden
        </AppButton>
      </View>
    </Screen>
  );
}
